import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { ILocationResponse } from '@kcftech/prometheustypescriptapiclient';
import { useFeatureFlag } from 'configcat-react';
import { usePermissions } from 'contexts/PermissionsContext';
import { LocationAssetsWithAlertsWidget } from 'pages/Location/alerts_and_drafts/assets_with_alerts/LocationAssetsWithAlertsWidget';
import { DraftIssuesWidget } from 'pages/Location/alerts_and_drafts/draft_issues/DraftIssuesWidget';
import {
    ExternalLinkIcon,
    HeaderRightContainer,
    StyledLink,
    Tab,
    TabList,
    TabPanel,
    Tabs,
    TabsHeaderContainer,
    WidgetContainer,
} from 'pages/Location/alerts_and_drafts/styled';
import { WorkbenchLink } from 'pages/Location/alerts_and_drafts/WorkbenchLink';
import { VerticalDivider } from 'pages/Location/issues/styled';
import { forwardRef, memo, useImperativeHandle, useRef, useState } from 'react';
import { OverviewStatusLozenge } from 'shared/lozenge/OverviewStatusLozenge';
import { getLocationMachineHealthDeskBaseRoute } from 'utils/RouteHelper';

export type AlertsAndDraftsWidgetRef = {
    refetch: () => Promise<unknown>;
};

type AlertsAndDraftsWidgetProps = {
    location: ILocationResponse;
};

function AlertsAndDraftsWidgetImpl(
    { location }: AlertsAndDraftsWidgetProps,
    ref: React.ForwardedRef<AlertsAndDraftsWidgetRef>
) {
    const { value: canViewDraftIssuesFeatureFlag } = useFeatureFlag(
        'draftIssuesLocationOverview',
        false
    );
    const {
        issue: { canViewDraftIssues },
    } = usePermissions();
    const [tabIndex, setTabIndex] = useState(0);

    const locationAssetsWithAlertsRef = useRef<{ refetch: () => Promise<unknown> }>(null);
    const draftIssuesRef = useRef<{ refetch: () => Promise<unknown> }>(null);

    useImperativeHandle(
        ref,
        () => ({
            refetch: async () => {
                // Refresh alerts count and widget data
                await Promise.all(
                    [
                        locationAssetsWithAlertsRef.current?.refetch(),
                        canViewDraftIssues('machineHealth') &&
                            draftIssuesRef.current?.refetch(),
                    ].filter(Boolean)
                );
            },
        }),
        [canViewDraftIssues]
    );

    if (canViewDraftIssues('machineHealth') && canViewDraftIssuesFeatureFlag) {
        return (
            <WidgetContainer>
                <Tabs selectedIndex={tabIndex} onSelect={setTabIndex}>
                    <TabsHeaderContainer>
                        <TabList>
                            <Tab>Draft Issues</Tab>
                            <Tab>Assets with Alerts</Tab>
                        </TabList>
                        <HeaderRightContainer>
                            <OverviewStatusLozenge>PAST 30 DAYS</OverviewStatusLozenge>
                            <VerticalDivider />
                            {tabIndex === 0 ? (
                                <StyledLink
                                    href={getLocationMachineHealthDeskBaseRoute(
                                        location.Id,
                                        'draft'
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    See all Drafts
                                    <ExternalLinkIcon icon={faExternalLinkAlt} />
                                </StyledLink>
                            ) : (
                                <WorkbenchLink locationId={location.Id} />
                            )}
                        </HeaderRightContainer>
                    </TabsHeaderContainer>
                    <TabPanel>
                        <DraftIssuesWidget
                            ref={draftIssuesRef}
                            locationId={location.Id}
                        />
                    </TabPanel>
                    <TabPanel>
                        <LocationAssetsWithAlertsWidget
                            ref={locationAssetsWithAlertsRef}
                            locationId={location.Id}
                            hideHeader={true}
                        />
                    </TabPanel>
                </Tabs>
            </WidgetContainer>
        );
    }

    return (
        <WidgetContainer>
            <LocationAssetsWithAlertsWidget
                ref={locationAssetsWithAlertsRef}
                locationId={location.Id}
            />
        </WidgetContainer>
    );
}

const memoAlertsAndDraftsWidget = memo(
    forwardRef<AlertsAndDraftsWidgetRef, AlertsAndDraftsWidgetProps>(
        AlertsAndDraftsWidgetImpl
    )
);
export { memoAlertsAndDraftsWidget as AlertsAndDraftsWidget };
